import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  listMyPayments,
  MY_PAYMENTS_QUERY_KEY,
  normalizePaymentList,
} from "@/api/paymentApi";
import { listMyRefunds, MY_REFUNDS_QUERY_KEY } from "@/api/refundApi";
import { PageHeader } from "@/components/common/PageHeader";
import type { RefundRequest, RefundStatus } from "@/types/domain";

const statusColor: Record<string, string> = {
  CAPTURED: "success.main",
  CREATED: "warning.main",
  FAILED: "error.main",
  ABANDONED: "text.secondary",
};

const refundChipColor: Record<
  RefundStatus,
  "default" | "warning" | "success" | "error"
> = {
  PENDING: "warning",
  PROCESSED: "success",
  REJECTED: "default",
  FAILED: "error",
};

const refundChipLabel: Record<RefundStatus, string> = {
  PENDING: "Refund requested",
  PROCESSED: "Refunded",
  REJECTED: "Refund declined",
  FAILED: "Refund retrying",
};

export default function PaymentsPage() {
  const { data, isLoading } = useQuery({
    queryKey: MY_PAYMENTS_QUERY_KEY,
    queryFn: listMyPayments,
  });

  const { data: refunds } = useQuery({
    queryKey: MY_REFUNDS_QUERY_KEY,
    queryFn: listMyRefunds,
  });

  /** Always normalize: cache may still hold a Spring `Page` from older builds. */
  const payments = useMemo(() => normalizePaymentList(data), [data]);

  /** Index latest refund per payment id so rows can show a "Refund …" chip. */
  const refundByPaymentId = useMemo(() => {
    const out = new Map<string, RefundRequest>();
    for (const r of refunds ?? []) {
      if (!out.has(r.paymentId)) out.set(r.paymentId, r);
    }
    return out;
  }, [refunds]);

  return (
    <Box>
      <PageHeader
        title="Payments"
        subtitle="All your consultation payments — invoices and refunds."
      />

      {isLoading && (
        <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && payments.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No payments yet.</Typography>
        </Paper>
      )}

      <Stack spacing={1.5}>
        {payments.map((p) => {
          const refund = refundByPaymentId.get(p.id);
          return (
            <Paper key={p.id} variant="outlined" sx={{ p: 2.5 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box>
                  <Typography fontWeight={700}>
                    ₹{(p.amount / 100).toFixed(2)} {p.currency}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {p.createdAt
                      ? format(new Date(p.createdAt), "d MMM yyyy · h:mm a")
                      : ""}
                  </Typography>
                  {p.razorpayPaymentId && (
                    <Typography variant="caption" color="text.secondary">
                      Payment ref: {p.razorpayPaymentId}
                    </Typography>
                  )}
                  {refund && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {refund.status === "PROCESSED"
                        ? `Refund of ₹${(refund.amountPaise / 100).toFixed(
                            2,
                          )} processed${
                            refund.processedAt
                              ? ` on ${format(
                                  new Date(refund.processedAt),
                                  "d MMM yyyy",
                                )}`
                              : ""
                          }.`
                        : refund.status === "REJECTED"
                          ? `Refund declined${refund.rejectionReason ? `: ${refund.rejectionReason}` : "."}`
                          : refund.status === "PENDING"
                            ? "Your refund request is awaiting admin review."
                            : "Refund is being retried — we'll email you once it's done."}
                    </Typography>
                  )}
                </Box>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <Typography
                    fontWeight={700}
                    sx={{ color: statusColor[p.status] ?? "text.primary" }}
                  >
                    {p.status}
                  </Typography>
                  {refund && (
                    <Chip
                      size="small"
                      color={refundChipColor[refund.status]}
                      label={refundChipLabel[refund.status]}
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
